import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import restaurantService, { UserOrderDetails } from '../../services/restaurantService';
import { formatDeliveryAddressLine } from '../../services/deliveryService';
import Toast from '../Toast/Toast';
import OrderFeedbackModal from '../OrderFeedback/OrderFeedbackModal';
import { isCompletedOrderStatus, itemStatusLabel, orderStatusLabel, summarizeItemStatuses } from '../../utils/orderStatus';
import { useRealtime } from '../../contexts/RealtimeContext';
import useOrderPolling from '../../hooks/useOrderPolling';
import { hasCustomerFeature } from '../../utils/planFeatures';
import { PlanFeaturesPayload } from '../../services/restaurantService';
import { useAppSelector } from '../../store/hooks';
import { useKioskMode } from '../../contexts/KioskModeContext';
import { kioskMenuPath, kioskOrdersPath } from '../Kiosk/kioskPaths';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import '../shared/ordersPageShell.css';
import './MyOrders.css';
import OrderLineOptions from '../common/OrderLineOptions/OrderLineOptions';
import MyOrdersSkeleton from './MyOrdersSkeleton';
import GuestInvoiceDownload from '../common/GuestInvoiceDownload/GuestInvoiceDownload';
import { guestItemGroups } from '../../utils/guestItemGroups';

const MyOrders: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, authReady, user: signedInUser } = useAppSelector((state) => state.user);
  const { enabled: kioskEnabled } = useKioskMode();
  const guestOrderType = useGuestOrderType(appName);
  const browseMenuPath = useMemo(() => {
    if (!appName) return '/restaurants';
    if (kioskEnabled) {
      return withGuestOrderTypeQuery(kioskMenuPath(appName), appName, guestOrderType);
    }
    return `/restaurants/${appName}/menu`;
  }, [appName, guestOrderType, kioskEnabled]);
  const [activeTab, setActiveTab] = useState<'ongoing' | 'history'>('ongoing');
  const [ongoingOrders, setOngoingOrders] = useState<UserOrderDetails[]>([]);
  const [historyOrders, setHistoryOrders] = useState<UserOrderDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('error');
  const [feedbackModalOrderId, setFeedbackModalOrderId] = useState<number | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(new Set());
  const [isWideLayout, setIsWideLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1025px)').matches
  );
  const { subscribeOrderStatus, rewardBadgeCount } = useRealtime();
  const [planFeatures, setPlanFeatures] = useState<PlanFeaturesPayload | undefined>(undefined);
  const [restaurantName, setRestaurantName] = useState('');

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1025px)');
    const onChange = (e: MediaQueryListEvent) => setIsWideLayout(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const fetchOrders = useCallback(async (type: 'ongoing' | 'history') => {
    setLoading(true);
    setError(null);
    try {
      const response = await restaurantService.getAllMyOrders(type);
      if (response.success && response.orders) {
        if (type === 'ongoing') {
          setOngoingOrders(response.orders);
        } else {
          setHistoryOrders(response.orders);
        }
      } else {
        setError(response.message || 'Failed to load orders');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while loading orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!authReady) {
      return;
    }
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    fetchOrders('ongoing');
    fetchOrders('history');

    const fetchRestaurant = async () => {
      if (!appName) return;
      try {
        const response = await restaurantService.getRestaurant(appName);
        if (response.features) {
          setPlanFeatures(response.features);
        }
        if (response.restaurant?.restaurantName) {
          setRestaurantName(response.restaurant.restaurantName);
        }
      } catch {
        // use default
      }
    };
    fetchRestaurant();
  }, [fetchOrders, appName, authReady, isAuthenticated]);

  const pollOngoingOrders = useCallback(async () => {
    try {
      const response = await restaurantService.getAllMyOrders('ongoing');
      if (response.success && response.orders) {
        setOngoingOrders(response.orders);
      }
    } catch {
      // silent refresh
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'ongoing') return undefined;
    return subscribeOrderStatus(() => {
      pollOngoingOrders();
    });
  }, [activeTab, subscribeOrderStatus, pollOngoingOrders]);

  useOrderPolling(pollOngoingOrders, activeTab === 'ongoing');

  const decodeCurrency = (symbol?: string): string => {
    if (!symbol) return '₹';
    if (symbol.includes('&#')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = symbol;
      return tempDiv.textContent || tempDiv.innerText || '₹';
    }
    return symbol;
  };

  const formatPrice = (price: number | undefined | null, currencySymbol?: string): string => {
    const sym = decodeCurrency(currencySymbol);
    if (price === undefined || price === null || isNaN(Number(price))) {
      return `${sym}0.00`;
    }
    const numericPrice = typeof price === 'number' ? price : parseFloat(String(price));
    return `${sym}${numericPrice.toFixed(2)}`;
  };

  const resolveOrderDisplayTotal = (order: UserOrderDetails): number => {
    const billing = order.billingDetails;
    const billingTotal = billing?.grandTotal;
    if (billingTotal != null && billingTotal > 0) {
      return billingTotal;
    }
    const userTotal = order.userOrder?.grandTotal;
    if (userTotal != null && userTotal > 0) {
      return userTotal;
    }
    const subtotal = billing?.subTotal ?? order.userOrder?.subTotal;
    if (subtotal != null && subtotal > 0) {
      return subtotal;
    }
    return 0;
  };

  const getOrderTypeLabel = (orderType: number): string => {
    switch (orderType) {
      case 1:
        return 'DINE-IN';
      case 2:
        return 'DELIVERY';
      case 3:
        return 'TAKE-AWAY';
      default:
        return 'ORDER';
    }
  };

  const getOrderStatusLabel = (status: string): string => {
    return orderStatusLabel(status);
  };

  const getOrderStatusClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('placed')) return 'status-placed';
    if (statusLower.includes('modified')) return 'status-modified';
    if (statusLower.includes('prepared') || statusLower.includes('ready')) return 'status-ready';
    if (statusLower.includes('completed') || statusLower.includes('served')) return 'status-placed';
    if (statusLower.includes('cancelled') || statusLower.includes('failed')) return 'status-cancelled';
    return 'status-default';
  };

  const handleTabChange = (tab: 'ongoing' | 'history') => {
    setActiveTab(tab);
  };

  const resolveOrderAppName = (order: UserOrderDetails): string | undefined =>
    order.restaurantAppName || appName;

  const handleViewOrder = (order: UserOrderDetails) => {
    const destApp = resolveOrderAppName(order);
    if (!destApp) return;
    if (kioskEnabled) {
      navigate(kioskOrdersPath(destApp, order.orderId));
      return;
    }
    navigate(`/restaurants/${destApp}/orders/${order.orderId}`);
  };

  const formatDropAddress = (order: UserOrderDetails): string => {
    if (order.userOrder?.orderType !== 2) {
      return '';
    }
    return (
      formatDeliveryAddressLine(order.userDeliveryDetails) ||
      order.userOrderDeliveryDetails?.destination ||
      ''
    );
  };

  const toggleOrderExpanded = (orderId: number) => {
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const formatOrderDate = (createdOn?: string | Date, compact = false): string => {
    if (!createdOn) return 'Date not available';
    const date = new Date(createdOn);
    if (compact) {
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const breadcrumbItems = appName
    ? kioskEnabled
      ? [
          { label: 'Menu', path: browseMenuPath },
          { label: 'Your Orders', active: true },
        ]
      : [
          { label: restaurantName || 'Restaurant', path: `/restaurants/${appName}` },
          { label: 'Menu', path: `/restaurants/${appName}/menu` },
          { label: 'Your Orders', active: true },
        ]
    : [{ label: 'Home', path: '/' }, { label: 'Your Orders', active: true }];

  const renderFeedbackActions = (order: UserOrderDetails, isOngoing: boolean) => {
    const { userOrder, feedbackSummary } = order;
    if (
      isOngoing ||
      !isCompletedOrderStatus(userOrder.orderStatus) ||
      !feedbackSummary
    ) {
      return null;
    }

    if (feedbackSummary.submitted) {
      if (feedbackSummary.canEdit) {
        return (
          <button
            type="button"
            className="order-feedback-cta"
            onClick={() => setFeedbackModalOrderId(order.orderId)}
          >
            Edit feedback
          </button>
        );
      }
      return (
        <span className="order-feedback-badge">
          <i className="fa fa-check" aria-hidden /> Feedback submitted
        </span>
      );
    }

    if (feedbackSummary.eligible) {
      return (
        <button
          type="button"
          className="order-feedback-cta"
          onClick={() => setFeedbackModalOrderId(order.orderId)}
        >
          Rate your experience
        </button>
      );
    }

    return null;
  };

  const renderOrderCard = (order: UserOrderDetails) => {
    const { userOrder, userOrderItemsList } = order;
    const isOngoing = activeTab === 'ongoing';
    const currency = userOrder.currencySymbol;
    const isExpanded = isWideLayout || expandedOrderIds.has(order.orderId);
    const orderCode = userOrder.orderCode || String(order.orderId);
    const items = userOrderItemsList || [];
    const itemGroups = guestItemGroups(items, signedInUser?.id);
    const displayTotal = resolveOrderDisplayTotal(order);
    const itemCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const hasItemStatuses = items.some((i) => (i as { status?: string }).status);
    const summaryLine = [
      `${itemCount} item${itemCount !== 1 ? 's' : ''}`,
      getOrderTypeLabel(userOrder.orderType),
      `#${orderCode}`,
    ].join(' · ');

    return (
      <article
        key={order.orderId}
        className={`order-card${isExpanded ? ' order-card--expanded' : ''}`}
      >
        <button
          type="button"
          className="order-card-summary"
          onClick={() => !isWideLayout && toggleOrderExpanded(order.orderId)}
          aria-expanded={isExpanded}
          aria-controls={`order-details-${order.orderId}`}
          disabled={isWideLayout}
        >
          <div className="order-card-summary__main">
            <div className="order-card-summary__status-row">
              {isOngoing && <span className="order-card-live-dot" aria-hidden />}
              <span className={`status-pill ${getOrderStatusClass(userOrder.orderStatus)}`}>
                {getOrderStatusLabel(userOrder.orderStatus)}
              </span>
              {(userOrder.orderStatus === 'ORDER_CONFIRMED' || userOrder.orderStatus === 'ORDER_PREPARING') &&
                (userOrder.estimatedPrepMinutes ?? userOrder.approxWaitTime) != null && (
                <span className="order-eta-hint">
                  ~{userOrder.estimatedPrepMinutes ?? userOrder.approxWaitTime} min
                </span>
              )}
              <span className="type-badge type-badge--compact">{getOrderTypeLabel(userOrder.orderType)}</span>
              {hasCustomerFeature(planFeatures, 'loyalty_rewards') && isCompletedOrderStatus(userOrder.orderStatus) && resolveOrderAppName(order) && (
                <span
                  title="Reward card issued — tap to view"
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: 99,
                    background: '#FFF0EE',
                    color: '#D9472B',
                    border: '1px solid #D9472B55',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/restaurants/${resolveOrderAppName(order)}/rewards`);
                  }}
                >
                  🎁 Reward
                </span>
              )}
            </div>
            {order.restaurantName && (
              <p className="order-card-summary__restaurant">
                {order.restaurantName}
              </p>
            )}
            {formatDropAddress(order) && (
              <p className="order-card-summary__drop">
                {order.userDeliveryDetails?.addressType
                  ? `${order.userDeliveryDetails.addressType} · ${formatDropAddress(order)}`
                  : formatDropAddress(order)}
              </p>
            )}
            <p className="order-card-summary__meta">{summaryLine}</p>
            <p className="order-card-summary__date">
              <i className="fa fa-clock-o" aria-hidden />
              {formatOrderDate(userOrder.createdOn, true)}
            </p>
          </div>
          <div className="order-card-summary__aside">
            <span className="order-card-summary__total">
              {formatPrice(displayTotal, currency)}
            </span>
            <i
              className={`fa fa-chevron-down order-card-chevron${isExpanded ? ' order-card-chevron--open' : ''}`}
              aria-hidden
            />
          </div>
        </button>

        <div
          id={`order-details-${order.orderId}`}
          className="order-card-details"
          hidden={!isExpanded}
        >
          <div className="order-card-meta-row">
            <span className="order-time order-time--full">
              <i className="fa fa-clock-o" aria-hidden />
              {formatOrderDate(userOrder.createdOn)}
            </span>
            {order.restaurantName && (
              <span className="table-chip">
                <i className="fa fa-cutlery" aria-hidden /> {order.restaurantName}
              </span>
            )}
            {formatDropAddress(order) && (
              <span className="item-status-summary">
                {order.userDeliveryDetails?.addressType
                  ? `${order.userDeliveryDetails.addressType} · ${formatDropAddress(order)}`
                  : formatDropAddress(order)}
              </span>
            )}
            {userOrder.tableNumber && (
              <span className="table-chip">
                <i className="fa fa-cutlery" aria-hidden /> {userOrder.spaceLabel || `Table ${userOrder.tableNumber}`}
              </span>
            )}
            {hasItemStatuses && (() => {
              const summary = summarizeItemStatuses(
                items.map((i) => ({
                  status: (i as { status?: string }).status,
                  active: (i as { active?: boolean }).active !== false,
                }))
              );
              return summary ? <span className="item-status-summary">{summary}</span> : null;
            })()}
            {isOngoing && userOrder.isSharedOrder && userOrder.sharePin && (
              <button
                type="button"
                className="share-pin-chip"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(userOrder.sharePin!);
                  setToastMessage(`PIN ${userOrder.sharePin} copied!`);
                  setToastType('info');
                }}
              >
                <i className="fa fa-share-alt" aria-hidden />
                Share PIN: <span className="pin-num">{userOrder.sharePin}</span>
              </button>
            )}
          </div>

          {items.length > 0 ? (
            <>
              <ul className="order-card-item-list order-card-item-list--mobile" aria-label="Order items">
                {(itemGroups || [{ key: 'all', label: '', isYou: false, isStaff: false, items }]).map((group) => (
                  <React.Fragment key={group.key}>
                    {itemGroups && (
                      <li className="order-card-guest-head">
                        <i className={`fa ${group.isStaff ? 'fa-cutlery' : 'fa-user'}`} aria-hidden />
                        <span>{group.label}</span>
                        {group.isYou ? <span className="order-card-guest-head__you">You</span> : null}
                        {group.isStaff && group.label !== 'Restaurant' ? (
                          <span className="order-card-guest-head__role">Restaurant</span>
                        ) : null}
                      </li>
                    )}
                    {group.items.map((item, index) => (
                      <li key={item.id ?? index} className="order-card-item-list__row">
                        <span className="order-card-item-list__name">
                          {item.quantity}x {item.itemName}
                          <OrderLineOptions optionsNote={item.optionsNote} itemNote={item.itemNote} />
                        </span>
                        {isOngoing && (
                          <span className="order-card-item-list__status">
                            {(item as { status?: string }).status
                              ? itemStatusLabel((item as { status?: string }).status)
                              : '—'}
                          </span>
                        )}
                        <span className="order-card-item-list__total">
                          {formatPrice(item.totalPrice, currency)}
                        </span>
                      </li>
                    ))}
                  </React.Fragment>
                ))}
              </ul>

              <div className="order-card-items order-card-items--desktop" aria-label="Order items">
                <div className="mini-table-header">
                  <span>Item</span>
                  <span>Qty</span>
                  <span>Total</span>
                </div>
                {(itemGroups || [{ key: 'all', label: '', isYou: false, isStaff: false, items }]).map((group) => (
                  <React.Fragment key={group.key}>
                    {itemGroups && (
                      <div className="order-card-guest-head">
                        <i className={`fa ${group.isStaff ? 'fa-cutlery' : 'fa-user'}`} aria-hidden />
                        <span>{group.label}</span>
                        {group.isYou ? <span className="order-card-guest-head__you">You</span> : null}
                        {group.isStaff && group.label !== 'Restaurant' ? (
                          <span className="order-card-guest-head__role">Restaurant</span>
                        ) : null}
                      </div>
                    )}
                    {group.items.map((item, index) => (
                      <div key={item.id ?? index} className="mini-item-row">
                        <div className="mini-item-name-block">
                          <span className="mini-item-name">{item.itemName}</span>
                          <OrderLineOptions optionsNote={item.optionsNote} itemNote={item.itemNote} />
                          <span className="mini-item-sub">
                            {formatPrice(item.price, currency)} each
                            {isOngoing && (item as { status?: string }).status && (
                              <>
                                {' · '}
                                {itemStatusLabel((item as { status?: string }).status)}
                              </>
                            )}
                          </span>
                        </div>
                        <span className="mini-item-val">{item.quantity}</span>
                        <span className="mini-item-val total">
                          {formatPrice(item.totalPrice, currency)}
                        </span>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </>
          ) : (
            <p className="order-card-empty-items">No items</p>
          )}
        </div>

        <div className="order-card-footer">
          <div className="order-card-footer__total">
            <span className="order-total-label">Total Amount</span>
            <span className="order-total-val">
              {formatPrice(displayTotal, currency)}
            </span>
          </div>
          <div className="order-card-footer__actions">
            {renderFeedbackActions(order, isOngoing)}
            {!isOngoing && isCompletedOrderStatus(userOrder.orderStatus) && resolveOrderAppName(order) && (
              <GuestInvoiceDownload
                appName={resolveOrderAppName(order)!}
                orderId={order.orderId}
                invoiceDownload={order.invoiceDownload}
                compact
              />
            )}
            <button
              type="button"
              className="view-details-btn"
              onClick={() => handleViewOrder(order)}
            >
              View Details <i className="fa fa-arrow-right" aria-hidden />
            </button>
          </div>
        </div>
      </article>
    );
  };

  const activeOrders = activeTab === 'ongoing' ? ongoingOrders : historyOrders;

  if (authReady && !isAuthenticated) {
    return (
      <div className="my-orders-page">
        {!kioskEnabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
            <Breadcrumb items={breadcrumbItems} />
            <div className="account-page-header">
              <h1 className="account-page-title">Your Orders</h1>
            </div>
            <div className="my-orders-container">
              <div className="empty-state">
                <i className="fa fa-list-alt empty-icon" aria-hidden />
                <h3>Sign in to see your orders</h3>
                <p>Sign in to see orders from every restaurant you have ordered from.</p>
                <Link to="/signin?redirect=/my-orders" className="cta-btn">
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </PageContainer>
        {!kioskEnabled && <Footer />}
      </div>
    );
  }

  if (loading && ongoingOrders.length === 0 && historyOrders.length === 0) {
    return (
      <div className="my-orders-page">
        {!kioskEnabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
          <Breadcrumb items={breadcrumbItems} />
          <div className="account-page-header">
            <h1 className="account-page-title">Your Orders</h1>
          </div>
          <div className="my-orders-container">
            <MyOrdersSkeleton cardCount={3} />
          </div>
          </div>
        </PageContainer>
        {!kioskEnabled && <Footer />}
      </div>
    );
  }

  return (
    <div className="my-orders-page">
      {!kioskEnabled && <Header />}
      <PageContainer>
        <div className="orders-page-shell">
        <Breadcrumb items={breadcrumbItems} />
        <div className="account-page-header">
          <h1 className="account-page-title">Your Orders</h1>
        </div>
        {hasCustomerFeature(planFeatures, 'loyalty_rewards') && rewardBadgeCount > 0 && appName && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/restaurants/${appName}/rewards`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/restaurants/${appName}/rewards`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'linear-gradient(90deg,#FFF8E1,#FFF0EE)',
              border: '1px solid #F5C21A',
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 14,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            <span style={{ fontSize: 20 }}>🎁</span>
            <span>
              You have {rewardBadgeCount} unscratched reward{rewardBadgeCount > 1 ? 's' : ''}!
              <span style={{ color: '#D9472B', marginLeft: 6, fontWeight: 700 }}>Scratch now →</span>
            </span>
          </div>
        )}
        <div className="my-orders-container">
          <div className="orders-content-wrapper">
            <div className="orders-tabs">
              <button
                type="button"
                className={`tab-button ${activeTab === 'ongoing' ? 'active' : ''}`}
                onClick={() => handleTabChange('ongoing')}
              >
                Ongoing Orders
                <span className="tab-count tab-count-active">{ongoingOrders.length}</span>
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => handleTabChange('history')}
              >
                Order History
                <span className="tab-count">{historyOrders.length}</span>
              </button>
            </div>

            {error && (
              <div className="error-message-wrapper">
                <div className="error-message">{error}</div>
              </div>
            )}

            <div className="orders-body">
              <div className="orders-list">
                {activeOrders.length > 0 ? (
                  activeOrders.map((order) => renderOrderCard(order))
                ) : (
                  <div className="empty-state">
                    <i
                      className={`fa ${activeTab === 'ongoing' ? 'fa-shopping-bag' : 'fa-history'} empty-icon`}
                      aria-hidden
                    />
                    <h3>
                      {activeTab === 'ongoing'
                        ? 'No ongoing orders'
                        : 'No order history yet'}
                    </h3>
                    <p>
                      {activeTab === 'ongoing'
                        ? "You don't have any active orders at the moment."
                        : 'Your past orders will appear here once completed.'}
                    </p>
                    <Link to={browseMenuPath} className="cta-btn">
                      {appName ? 'Browse Menu' : 'Browse restaurants'}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </PageContainer>
      {!kioskEnabled && <Footer />}

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}

      {(() => {
        const feedbackOrder =
          historyOrders.find((o) => o.orderId === feedbackModalOrderId) ||
          ongoingOrders.find((o) => o.orderId === feedbackModalOrderId);
        const feedbackApp = feedbackOrder?.restaurantAppName || appName;
        if (!feedbackApp || feedbackModalOrderId == null) {
          return null;
        }
        return (
          <OrderFeedbackModal
            appName={feedbackApp}
            orderId={feedbackModalOrderId}
            open
            onClose={() => setFeedbackModalOrderId(null)}
            onSubmitted={() => {
              fetchOrders('history');
              setFeedbackModalOrderId(null);
            }}
            onNotify={(message, type) => {
              setToastMessage(message);
              setToastType(type);
            }}
          />
        );
      })()}
    </div>
  );
};

export default MyOrders;
